import { api } from "@stoneflow/backend/convex/_generated/api"
import type { Priority, Status } from "@stoneflow/shared/validators/status"
import { createFileRoute } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { ClipboardList, Plus } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { LogRoleGate } from "#/components/capability-gate"
import { ExceptionFilters } from "#/components/exception-filters"
import type { ExceptionRow } from "#/components/exception-table"
import { ExceptionTable } from "#/components/exception-table"
import { RouteError } from "#/components/route-error"
import { ExceptionListSkeleton } from "#/components/skeletons/exception-list-skeleton"
import { Button } from "#/components/ui/button"
import { Card, CardContent } from "#/components/ui/card"
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "#/components/ui/empty"
import { useAppNavigate } from "#/hooks/use-app-navigate"
import { useOrgMembers, withYouSuffix } from "#/hooks/useOrgMembers"
import { useStonelog } from "#/lib/stonelog-context"

export const Route = createFileRoute("/_authenticated/stonelog/exceptions/")({
	errorComponent: RouteError,
	component: ExceptionList,
})

function ExceptionList() {
	const { currentLogId, currentLog } = useStonelog()
	const navigate = useAppNavigate()
	const { members, currentUserId } = useOrgMembers()

	const [search, setSearch] = useState("")
	const [statusFilter, setStatusFilter] = useState<Status | "all">("all")
	const [priorityFilter, setPriorityFilter] = useState<Priority | "all">("all")
	const [typeFilter, setTypeFilter] = useState<string>("all")
	const [raisedByFilter, setRaisedByFilter] = useState<string>("all")
	const [assignedToFilter, setAssignedToFilter] = useState<string>("all")
	const [departmentFilter, setDepartmentFilter] = useState<string>("all")

	const handleClearFilters = () => {
		setSearch("")
		setStatusFilter("all")
		setPriorityFilter("all")
		setTypeFilter("all")
		setRaisedByFilter("all")
		setAssignedToFilter("all")
		setDepartmentFilter("all")
	}

	const exceptions = useQuery(
		api.stonelog.exceptions.list,
		currentLogId ? { logId: currentLogId } : "skip",
	)
	const exceptionTypes = useQuery(api.stonelog.exceptionTypes.list, {})
	const categories = useQuery(
		api.stonelog.categories.listByLogType,
		currentLog?.logTypeId ? { logTypeId: currentLog.logTypeId } : "skip",
	)
	const departments = useQuery(
		api.stonelog.departments.listForLog,
		currentLogId ? { logId: currentLogId } : "skip",
	)

	const typeMap = useMemo(() => {
		const m = new Map<string, string>()
		for (const t of exceptionTypes ?? []) m.set(t._id, t.name)
		return m
	}, [exceptionTypes])

	const users = useMemo(
		() =>
			(members ?? []).map((m) => ({
				id: m.userId,
				label: withYouSuffix(m.name, m.userId, currentUserId),
			})),
		[members, currentUserId],
	)

	useEffect(() => {
		if (!currentLogId) navigate({ to: "/stonelog/select-log" })
	}, [currentLogId, navigate])

	if (!currentLogId) return null

	if (exceptions === undefined) {
		return <ExceptionListSkeleton />
	}

	const filtered = (exceptions ?? []).filter((e) => {
		if (
			search &&
			!e.reference.toLowerCase().includes(search.toLowerCase()) &&
			!e.description.toLowerCase().includes(search.toLowerCase())
		) {
			return false
		}
		if (statusFilter !== "all" && e.status !== statusFilter) return false
		if (priorityFilter !== "all" && e.priority !== priorityFilter) return false
		if (typeFilter !== "all" && e.typeId !== typeFilter) return false
		if (raisedByFilter !== "all" && e.raisedByUserId !== raisedByFilter) return false
		if (assignedToFilter !== "all" && e.assignedUserId !== assignedToFilter) return false
		if (departmentFilter !== "all" && e.assignedDepartmentId !== departmentFilter) return false
		return true
	})

	const rows: ExceptionRow[] = filtered.map((e) => ({
		_id: e._id,
		reference: e.reference,
		typeName: ("typeName" in e ? (e.typeName as string) : null) ?? typeMap.get(e.typeId) ?? "—",
		description: e.description,
		raisedByUserId: e.raisedByUserId,
		raisedByName: withYouSuffix(
			e.raisedByName ?? members.find((m) => m.userId === e.raisedByUserId)?.name ?? "Unknown",
			e.raisedByUserId,
			currentUserId,
		),
		assignedUserId: e.assignedUserId,
		assignedUserName: e.assignedUserId
			? withYouSuffix(
					members.find((m) => m.userId === e.assignedUserId)?.name ?? e.assignedUserId,
					e.assignedUserId,
					currentUserId,
				)
			: undefined,
		status: e.status as Status,
		priority: e.priority as Priority,
		_creationTime: e._creationTime,
	}))

	return (
		<div className="mx-auto max-w-6xl space-y-4 p-6 overflow-x-hidden">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h1>{currentLog?.name ?? "Exceptions"} Exceptions</h1>
					<p className="text-sm text-muted-foreground mt-1">{filtered.length} total exceptions</p>
				</div>
				<LogRoleGate logId={currentLogId} minRole="editor">
					<Button
						className="w-full gap-2 sm:w-auto"
						onClick={() => navigate({ to: "/stonelog/exceptions/new" as string })}
					>
						<Plus className="size-4" />
						New Exception
					</Button>
				</LogRoleGate>
			</div>

			<Card>
				<CardContent className="pt-6">
					<ExceptionFilters
						search={search}
						onSearchChange={setSearch}
						statusFilter={statusFilter}
						onStatusChange={setStatusFilter}
						priorityFilter={priorityFilter}
						onPriorityChange={setPriorityFilter}
						typeFilter={typeFilter}
						onTypeChange={setTypeFilter}
						raisedByFilter={raisedByFilter}
						onRaisedByChange={setRaisedByFilter}
						assignedToFilter={assignedToFilter}
						onAssignedToChange={setAssignedToFilter}
						departmentFilter={departmentFilter}
						onDepartmentChange={setDepartmentFilter}
						exceptionTypes={(exceptionTypes ?? []).map((t) => ({
							_id: t._id,
							name: t.name,
							categoryId: t.categoryId,
						}))}
						categories={(categories ?? []).map((c) => ({ _id: c._id, name: c.name }))}
						users={users}
						departments={(departments ?? []).map((d) => ({ _id: d._id, name: d.name }))}
						onClearAll={handleClearFilters}
					/>
				</CardContent>
			</Card>

			<div className="rounded-lg border border-border bg-card overflow-hidden">
				{filtered.length === 0 ? (
					<Empty>
						<EmptyHeader>
							<EmptyMedia variant="icon">
								<ClipboardList />
							</EmptyMedia>
							<EmptyTitle>No exceptions found</EmptyTitle>
							<EmptyDescription>
								{search || statusFilter !== "all" || priorityFilter !== "all"
									? "Try adjusting your filters."
									: "Create your first exception to get started."}
							</EmptyDescription>
						</EmptyHeader>
					</Empty>
				) : (
					<ExceptionTable
						rows={rows}
						onRowClick={(id) =>
							navigate({
								to: "/stonelog/exceptions/$exceptionId" as string,
								params: { exceptionId: id },
							})
						}
					/>
				)}
			</div>
		</div>
	)
}
